type EventValue = string | number | boolean;
type EventParams = Record<string, EventValue | null | undefined>;

const DEFAULT_GA4_STRING_PARAM_LIMIT = 100;
const EXTENDED_GA4_STRING_PARAM_LIMITS: Partial<Record<string, number>> = {
	page_title: 300,
	page_url: 1000,
	page_path: 1000,
};

declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void;
		dataLayer?: unknown[];
	}
}

const isClient = () => typeof window !== "undefined";

const UTM_COOKIE_KEY_MAP = {
	utm_source: ["utm_source", "utmSource"],
	utm_medium: ["utm_medium", "utmMedium"],
	utm_campaign: ["utm_campaign", "utmCampaign"],
	utm_term: ["utm_term", "utmTerm"],
	utm_content: ["utm_content"],
	gclid: ["gclid"],
	fbclid: ["fbclid"],
	_fbc: ["_fbc"],
	_fbp: ["_fbp"],
} as const;

const sanitizeEventParamValue = (key: string, value: EventValue): EventValue => {
	if (typeof value !== "string") {
		return value;
	}

	const maxLength =
		EXTENDED_GA4_STRING_PARAM_LIMITS[key] || DEFAULT_GA4_STRING_PARAM_LIMIT;

	return value.slice(0, maxLength);
};

const cleanObject = (data: EventParams): Record<string, EventValue> => {
	return Object.entries(data).reduce<Record<string, EventValue>>((acc, [key, value]) => {
		if (value !== undefined && value !== null) {
			acc[key] = sanitizeEventParamValue(key, value);
		}
		return acc;
	}, {});
};

const readCookie = (name: string): string | undefined => {
	if (!isClient()) return undefined;
	const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
	if (!match?.[1]) return undefined;
	try {
		return decodeURIComponent(match[1]);
	} catch {
		return match[1];
	}
};

const readCookieByAlias = (aliases: readonly string[]): string | undefined => {
	for (const alias of aliases) {
		const value = readCookie(alias);
		if (value) return value;
	}
	return undefined;
};

const getPlatform = (): "MWEB" | "WEB" => {
	if (!isClient()) return "WEB";
	return window.innerWidth < 768 ? "MWEB" : "WEB";
};

const getBaseEventMeta = (): Record<string, EventValue> => {
	if (!isClient()) return {};
	const utmData = Object.entries(UTM_COOKIE_KEY_MAP).reduce<Record<string, EventValue>>(
		(acc, [key, aliases]) => {
			const value = readCookieByAlias(aliases);
			if (value) {
				acc[key] = value;
			}
			return acc;
		},
		{},
	);

	return {
		...utmData,
		page_url: window.location.href,
		page_path: window.location.pathname,
		page_title: document.title || "",
		platform: getPlatform(),
	};
};

const ensureGtag = (): ((...args: unknown[]) => void) | undefined => {
	if (!isClient()) return undefined;

	window.dataLayer = window.dataLayer || [];

	if (typeof window.gtag !== "function") {
		window.gtag = function gtagShim(..._args: unknown[]) {
			window.dataLayer?.push(arguments);
		};
	}

	return window.gtag;
};

const emitGaEvent = (
	eventName: string,
	eventProps: EventParams = {},
	userProps?: EventParams,
): void => {
	const finalEventProps = cleanObject({
		...getBaseEventMeta(),
		...eventProps,
	});

	const finalUserProps = userProps ? cleanObject(userProps) : undefined;

	const gtag = ensureGtag();

	if (!gtag) {
		return;
	}

	if (finalUserProps && Object.keys(finalUserProps).length > 0) {
		gtag("set", "user_properties", finalUserProps);
	}

	gtag("event", eventName, finalEventProps);
};

export const logPageView = (path?: string): void => {
	emitGaEvent("page_view", {
		page_path: path || (isClient() ? window.location.pathname : "/"),
	});
};

export const logEvent = (category: string, action: string, label?: string): void => {
	emitGaEvent(action, {
		category,
		action,
		label,
	});
};

export const handleGaEvent = (payload: Record<string, unknown>) => {
	const userProps: EventParams = {
		lead_stage: payload.leadStage as string | undefined,
		user_id: payload.userId as string | undefined,
		subscribed_to_whatsapp: payload.subscribedToWhatsapp as boolean | undefined,
		login_type: payload.authType as string | undefined,
	};
	const eventProps: EventParams = {
		page_name: payload.pageName as string | undefined,
		auth_type: payload.authType as string | undefined,
		subscribed_to_whatsapp: payload.subscribedToWhatsapp as boolean | undefined,
		login_type: payload.authType as string | undefined,
	};
	return sendCustomGaEvent("Log In Successful", eventProps, userProps);
};

export const sendPageViewGAEvent = (
	eventProps: EventParams,
	userProps?: EventParams,
) => {
	emitGaEvent("page_view", eventProps, userProps);
};

export function sendCustomGaEvent(
	eventName: string,
	eventProps: EventParams = {},
	userProps?: EventParams,
): Promise<void> {
	return new Promise((resolve) => {
		emitGaEvent(eventName, eventProps, userProps);
		resolve();
	});
}

export const sendCustomGaEventWithoutProperties = (
	eventName: string,
): Promise<void> => sendCustomGaEvent(eventName);

export const trackInteractionEvent = (
	eventName: string,
	eventProps: EventParams = {},
): void => {
	emitGaEvent(eventName, eventProps);
};

export const fireTagEvent = (eventName: string, eventData?: EventParams): void => {
	if (!isClient()) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: eventName,
		eventData: eventData ? cleanObject(eventData) : undefined,
	});
};

export const fireCountryTagEvent = (eventName: string, country?: string): void => {
	if (!isClient()) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({
		event: eventName,
		country,
	});
};

export const fireOptimizeEvent = (): void => {
	if (!isClient()) return;
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push({ event: "optimize.activate" });
};
